import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { PlusIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import { addPortfolio } from '@/features/portfolios/portfolios-api';

// Ports app/[brand]/admin/portfolios/_components/add-portfolio.tsx. The Next
// form used conform bound to a server-side form action; the SPA calls the
// async `addPortfolio` directly and tracks pending/errors with local state.
function AddPortfolioForm({ onSuccess }: { onSuccess: () => void }) {
  const t = useTranslations('admin');
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    slug?: string;
    form?: string;
  }>({});

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setErrors({});

    const formData = new FormData(event.currentTarget);
    const result = await addPortfolio({
      name: String(formData.get('name') ?? ''),
      slug: String(formData.get('slug') ?? ''),
    });

    setPending(false);
    if (result.status === 'success') {
      onSuccess();
      return;
    }
    setErrors(result.errors);
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-2">
        <Label htmlFor="name">{t('name')}</Label>
        <Input id="name" name="name" type="text" placeholder={t('name')} />
        {errors.name && (
          <p className="text-destructive text-sm">{errors.name}</p>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="slug">{t('slug')}</Label>
        <Input id="slug" name="slug" type="text" placeholder={t('slug')} />
        {errors.slug && (
          <p className="text-destructive text-sm">{errors.slug}</p>
        )}
      </div>
      {errors.form && <p className="text-destructive text-sm">{errors.form}</p>}
      <SubmitButton
        label={t('add', { what: t('portfolio').toLowerCase() })}
        pending={pending}
      />
    </form>
  );
}

export function AddPortfolio({ onAdded }: { onAdded: () => void }) {
  const t = useTranslations('admin');
  const title = t('add', { what: t('portfolio').toLowerCase() });
  const [open, setOpen] = useState(false);

  const onSuccess = () => {
    setOpen(false);
    onAdded();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-5 w-5" />
          {title}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <AddPortfolioForm onSuccess={onSuccess} />
      </DialogContent>
    </Dialog>
  );
}
